import React, { useCallback, useState } from 'react';

import { Box, Button, CenteredLayout, H1, IconButton, Text, WorkflowHeader } from '@betterment/samba-react';
import { getLegacySearchParam } from '@betterment/shared-components';
import { useSearchParams } from 'react-router-dom';

import EmailVerificationEmailResentDialog from './EmailVerificationEmailResentDialog';
import SplitFlow from './SplitFlow';
import VerificationCheckFailedDialog from './VerificationCheckFailedDialog';

import { trackClick } from '../../features/track';
import ShieldLockImage from '../../images/shield_lock.svg';
import { WithLayout } from '../../RetailApp/components/WithLayout';
import FooterWithErrorBoundary from '../../Shared/components-v2/footer/Footer';
import { locationAssign } from '../../Shared/path-helpers';
import useEmailVerification from '../hooks/useEmailVerification';

const EmailVerificationCheckpoint: React.FC = () => {
  const [searchParams] = useSearchParams();
  const exitTo = getLegacySearchParam(searchParams, 'exit_to');
  const returnTo = getLegacySearchParam(searchParams, 'return_to') || '/';

  const { isVerified, refetchIsVerified, sendEmailVerificationEmail, loading } = useEmailVerification();

  const [isVerificationCheckFailedDialogOpen, setIsVerificationCheckFailedDialogOpen] = useState(false);
  const [isVerificationEmailResendSuccessDialogOpen, setIsVerificationEmailResendSuccessDialogOpen] = useState(false);

  const checkVerification = useCallback(async () => {
    const result = await refetchIsVerified();
    if (result.data?.user?.isEmailVerificationEligible) setIsVerificationCheckFailedDialogOpen(true);
  }, [refetchIsVerified]);

  const attemptSendEmailVerificationEmail = useCallback(async () => {
    const result = await sendEmailVerificationEmail();
    if (result.data?.sendEmailVerificationEmail?.result?.wasEmailSent)
      setIsVerificationEmailResendSuccessDialogOpen(true);
  }, [sendEmailVerificationEmail]);

  if (!exitTo) throw new Error('Checkpoint requires a redirect path.');
  if (isVerified) locationAssign(exitTo);

  return (
    <WithLayout parts={{ sidebar: false, footer: false }}>
      <SplitFlow>
        <SplitFlow.Header>
          <WorkflowHeader css={{ maxWidth: '100%' }}>
            <WorkflowHeader.Leading />
            <WorkflowHeader.Title>Verification</WorkflowHeader.Title>
            <WorkflowHeader.Trailing>
              <IconButton
                label="Navigate to previous page"
                name="X"
                spacing="default"
                onClick={() => {
                  locationAssign(returnTo);
                }}
              />
            </WorkflowHeader.Trailing>
          </WorkflowHeader>
        </SplitFlow.Header>
        <SplitFlow.ImagePane>
          <SplitFlow.Image src={ShieldLockImage} alt="Padlock illustration" />
        </SplitFlow.ImagePane>
        <SplitFlow.Content>
          <CenteredLayout size="medium" css={{ gap: 'space.400', padding: '{spacing.space.800} {spacing.space.400}' }}>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
              <H1 variant="pageTitle">Verify your email to fund your account.</H1>
              <Text>Your account is ready! Just a few final steps to start putting your money to work:</Text>
            </Box>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
              <Text>1. Check your email and open the verification link we sent</Text>
              <Text>2. Link a bank account</Text>
              <Text>3. Make your first deposit</Text>
            </Box>
            <Button
              onClick={() => {
                trackClick({ name: 'ConfirmEmailVerification', module: 'EmailVerificationCheckpoint' });
                void checkVerification();
              }}
              disabled={loading || isVerified}
            >
              I’ve verified my email
            </Button>
            <Button
              variant="textPrimary"
              disabled={loading || isVerified}
              onClick={() => {
                trackClick({ name: 'RequestVerificationEmail', module: 'EmailVerificationCheckpoint' });
                void attemptSendEmailVerificationEmail();
              }}
            >
              Send verification again
            </Button>
          </CenteredLayout>
          <Box css={{ padding: 'space.400' }}>
            <FooterWithErrorBoundary />
          </Box>
        </SplitFlow.Content>
      </SplitFlow>
      <EmailVerificationEmailResentDialog
        isOpen={isVerificationEmailResendSuccessDialogOpen}
        setIsOpen={setIsVerificationEmailResendSuccessDialogOpen}
      />
      <VerificationCheckFailedDialog
        isOpen={isVerificationCheckFailedDialogOpen}
        setIsOpen={setIsVerificationCheckFailedDialogOpen}
      />
    </WithLayout>
  );
};

export default EmailVerificationCheckpoint;
